import { QuotaUsageType } from "@common-shared/config/oneVerify/oneVerifyCoverage.config"
import { IOneVerifyPracticeUsageQuotaExtended } from "@common-shared/types/responseData/oneVerifyResponseData.types"

import { ConfirmationHelper } from "@front-shared/helpers/confirmation.helper"

import { CHECKER_CONFIG } from "../config/checker.config"
import { COVERAGE_CONFIG } from "../config/coverage.config"
import { GENERAL_CONFIG } from "../config/general.config"
import type {
    IContinuousMonitoringCoverageCheck,
    IMedicaidCoverageCheck,
} from "../types/objects.types"
import { UtilHelper } from "./util.helper"

export class CheckerHelper {
    // Dispatch timestamps of the bulk checks still inside the rate cap window,
    // oldest first. A sliding log rather than a fixed window counter: resetting
    // a counter on a window boundary lets a late burst in one window sit next to
    // a full allowance in the next, which breaches the cap across the boundary.
    // Static so the allowance is shared by every bulk run on the page (bulk
    // checker and bulk recheck) instead of each run getting its own.
    private static bulkChecksLog: number[] = []

    /**
     * Run checks with quota confirmation popups if hit some limits
     */
    static runChecksWithQuotaConfirmation(
        usageQuota: IOneVerifyPracticeUsageQuotaExtended,
        onRunChecks: () => void | Promise<void>,
        quotaUsageType: QuotaUsageType,
        checksAmount: number,
    ) {
        // don't make any validation for ignored check types
        if (COVERAGE_CONFIG.usageQuota.ignoredTypes.includes(quotaUsageType)) {
            return onRunChecks()
        }

        const neededLeftQuotaAmount =
            usageQuota.checkTypeCreditsAmount[quotaUsageType] *
            Math.max(1, checksAmount)

        const isHardQuotaPotentialHit =
            usageQuota.hardQuotaLeft - neededLeftQuotaAmount < 0

        const isSoftQuotaPotentialHit =
            usageQuota.softQuotaLeft - neededLeftQuotaAmount < 0

        if (!isHardQuotaPotentialHit && !isSoftQuotaPotentialHit) {
            return onRunChecks()
        }

        const onContactSupport = () => {
            window.location.href = `mailto:${GENERAL_CONFIG.supportEmail}`
        }

        if (isHardQuotaPotentialHit) {
            ConfirmationHelper.enableConfirmation(
                onContactSupport,
                "OV Check limit will be exceeded",
                [
                    "Running this check will exceed the maximum number of additional OneVerify checks allowed for your organization this month.",
                    "Your organization's credits will reset on the 1st of next month.",
                    "Please note that API checks do not count towards the check limit.",
                    <div>
                        Email us at{" "}
                        <a href={`mailto:${GENERAL_CONFIG.supportEmail}`}>
                            {GENERAL_CONFIG.supportEmail}
                        </a>
                    </div>,
                ],
                undefined,
                "Contact support",
                undefined,
                false,
                {
                    isLeftAligned: true,
                    withCloseIcon: true,
                    hideConfirmButton: true,
                },
            )
        } else if (isSoftQuotaPotentialHit) {
            ConfirmationHelper.enableConfirmation(
                onRunChecks,
                "Monthly OV check limit reached",
                [
                    `Your organization has exceeded the ${new Intl.NumberFormat().format(
                        usageQuota.softQuotaLimit,
                    )} included OneVerify checks for this month.`,
                    "Additional checks will be charged at a premium rate until your limit resets on the 1st of next month.",
                    "Please note that API checks do not count towards the check limit.",
                ],
                onContactSupport,
                "Run check anyway",
                "Contact support",
                true,
                {
                    isLeftAligned: true,
                    withCloseIcon: true,
                },
            )
        }
    }

    /**
     * Get amount of quota that will be used by currently in progress checks
     */
    static getInProgressChecksQuotaAmount(
        checksType: QuotaUsageType,
        continuousMonitoringChecks: IContinuousMonitoringCoverageCheck[],
        medicaidChecks: IMedicaidCoverageCheck[],
        selectedPracticeUsageQuota: IOneVerifyPracticeUsageQuotaExtended | null,
    ): number {
        if (
            !selectedPracticeUsageQuota ||
            COVERAGE_CONFIG.usageQuota.ignoredTypes.includes(checksType)
        ) {
            return 0
        }

        const neededChecks =
            checksType === QuotaUsageType.MEDICAID
                ? medicaidChecks.filter(
                      item => !item.inputData.csvValidationErrors?.length,
                  )
                : continuousMonitoringChecks.filter(
                      item => !item.inputData.csvValidationErrors?.length,
                  )

        return (
            neededChecks.length *
            selectedPracticeUsageQuota.checkTypeCreditsAmount[checksType]
        )
    }

    /**
     * Wait until the bulk checks rate cap has room, then return how many checks
     * may be dispatched right now. Batches run back to back at full speed while
     * the allowance lasts; once it is spent this blocks until the oldest
     * dispatch ages out of the window, so no window-length span ever holds more
     * than the cap - including spans straddling two windows.
     */
    static async awaitBulkChecksCapacity(
        requestedAmount: number,
    ): Promise<number> {
        const limit = CHECKER_CONFIG.maxBulkChecksPerWindow
        const windowMs = CHECKER_CONFIG.bulkChecksWindowMs

        // Pacing is off unless both values are positive numbers. Written as
        // !(x > 0) so zero, negatives and NaN all opt out: a negative limit
        // would otherwise enter the wait loop on an empty log and spin.
        if (!(limit > 0) || !(windowMs > 0) || requestedAmount <= 0) {
            return requestedAmount
        }

        CheckerHelper.pruneBulkChecksLog(windowMs)

        // Allowance spent - wait for the oldest dispatch to age out, padded so an
        // early-firing timer still lands past the cutoff instead of costing
        // another pass. Since limit is positive, reaching it means the log is
        // non-empty, so [0] is always defined.
        while (CheckerHelper.bulkChecksLog.length >= limit) {
            const waitMs =
                CheckerHelper.bulkChecksLog[0] + windowMs - Date.now()

            if (waitMs > 0) {
                await UtilHelper.sleep(
                    (waitMs + CHECKER_CONFIG.bulkChecksTimerSlopMs) / 1000,
                )
            }

            CheckerHelper.pruneBulkChecksLog(windowMs)
        }

        // Checks dispatched together share a timestamp, so a batch ages out of
        // the window as one block. While maxBulkChecksPerWindow stays a multiple
        // of processBatchSize every grant is therefore a whole batch - capacity
        // is never handed out piecemeal. Keep that multiple relationship if
        // either value changes: with, say, a limit of 45 and a batch of 10 the
        // tail of each window is granted 5 at a time instead of 10.
        const allowedAmount = Math.min(
            requestedAmount,
            limit - CheckerHelper.bulkChecksLog.length,
        )

        const dispatchedAt = Date.now()

        for (let i = 0; i < allowedAmount; i++) {
            CheckerHelper.bulkChecksLog.push(dispatchedAt)
        }

        return allowedAmount
    }

    /**
     * Run items in rate-capped batches, awaiting each batch before asking for
     * capacity for the next one. Callers own per-item error handling: an
     * uncaught rejection aborts the rest of its batch.
     */
    static async runChecksInBatches<TItem>(
        items: TItem[],
        runOne: (item: TItem) => Promise<unknown>,
    ): Promise<void> {
        let processedCount = 0

        while (processedCount < items.length) {
            // Take what the rate cap still has room for. Blocks only once the
            // allowance is fully spent.
            const allowedBatchSize =
                await CheckerHelper.awaitBulkChecksCapacity(
                    Math.min(
                        CHECKER_CONFIG.processBatchSize,
                        items.length - processedCount,
                    ),
                )

            // Unreachable while the checker config is sane, but a non-positive
            // batch would never advance processedCount and would hang the tab
            if (allowedBatchSize <= 0) {
                break
            }

            const batch = items
                .slice(processedCount, processedCount + allowedBatchSize)
                .map(item => runOne(item))

            processedCount += allowedBatchSize

            await Promise.all(batch)
        }
    }

    /**
     * Drop bulk check dispatches that have aged out of the rate cap window.
     */
    private static pruneBulkChecksLog(windowMs: number): void {
        const cutoff = Date.now() - windowMs

        while (
            CheckerHelper.bulkChecksLog.length &&
            CheckerHelper.bulkChecksLog[0] <= cutoff
        ) {
            CheckerHelper.bulkChecksLog.shift()
        }
    }
}
